import React, {useContext, FC} from "react"
import connect from "@irati/store/connect"
import ReduxState from "@irati/store/types/ReduxState"
import utilsId from "@irati/utils/id"
import {LARGE_DESKTOP, DESKTOP, TABLET, PHONE_LANDSCAPE} from "@irati/ui/src/global/media"
import Slider from "@irati/ui/src/Slider"
import {Deal, Deal as DealType, DealsTag} from "@irati/types"

import DrawerContext from "@irati/ui/src/AppContainer/Context/DrawerContext"
import DealPages from "../../enums/DealPages"
import DefaultSectionHeader from "../SectionHeader"
import DealCard from "../Deal/DealCard"
import {getDealUrl} from "../../helpers/getDealUrl"
import GlobalStyle, {DealContainer} from "./styled/DealContainer"

export const SETTINGS_REGULAR = {
  centerPadding: "30px",
  slidesToShow: 4,
  slidesToScroll: 4,
  responsive: [
    {breakpoint: PHONE_LANDSCAPE, settings: {slidesToShow: 1.2, slidesToScroll: 1.2, centerPadding: "0 0 16%"}},
    {breakpoint: TABLET, settings: {slidesToShow: 1.4, slidesToScroll: 1.4, centerPadding: "0 0 28%"}},
    {breakpoint: DESKTOP, settings: {slidesToShow: 2, slidesToScroll: 2}},
    {breakpoint: LARGE_DESKTOP, settings: {slidesToShow: 3, slidesToScroll: 3}}
  ]
}

const SHARE_TITLE = "Perkbox deal"

interface Props {
  data: DealType[]
  tag: DealsTag
  id: string
  className: string
  logo: string
  userLocation: string
  isMobile: boolean
  showDealLocation: boolean
  capitaliseHeader: boolean
  SectionHeader: JSX.Element
  sectionHeaderText: string | JSX.Element
  keyExtractor: (item: {uuid: string}, key?: string | number) => string
  showSectionHeader: boolean
  seeMoreUrl?: string
  page?: DealPages
  sectionBodyText?: string
  isFavouriteDeal?: boolean
}

const FeedSliderGraph: FC<Props> = ({
  data = [],
  tag,
  className = "feed-slider",
  logo,
  isFavouriteDeal = false,
  userLocation,
  showDealLocation,
  capitaliseHeader = false,
  SectionHeader = DefaultSectionHeader,
  sectionHeaderText,
  sectionBodyText,
  keyExtractor = (item: {uuid: string}) => item.uuid,
  showSectionHeader = true,
  seeMoreUrl,
  page,
  isMobile = false,
  id: propsId
}) => {
  const {setDrawerOpen} = useContext(DrawerContext)
  const id = utilsId(className, propsId)
  const buildSettings = () => {
    const moreThanOne = data.length > 1
    return {
      ...SETTINGS_REGULAR,
      centerMode: moreThanOne && isMobile,
      infinite: moreThanOne && isMobile,
      draggable: moreThanOne && isMobile
    }
  }

  const handleShareClick = (deal: Deal) => {
    const drawerData = {
      url: getDealUrl(deal.urlSlug),
      title: SHARE_TITLE,
      alertText: "perks.deals.shareText",
      source: "perks",
      type: "card"
    }
    setDrawerOpen(drawerData)
  }

  if (!data.length) return null

  return (
    <div id={id}>
      <GlobalStyle />
      {showSectionHeader && (
        <SectionHeader
          id={id}
          tag={tag}
          logo={logo}
          userLocation={userLocation}
          capitalise={capitaliseHeader}
          headerText={sectionHeaderText}
          seeMoreUrl={seeMoreUrl}
          bodyText={sectionBodyText}
        />
      )}
      <div>
        <Slider id={id} className={className} settings={buildSettings()}>
          {data.map((item, key) => (
            <DealContainer key={keyExtractor(item)}>
              <DealCard
                isFavouriteDeal={isFavouriteDeal}
                showLocation={showDealLocation}
                id={utilsId(keyExtractor(item, key), id)}
                deal={item}
                page={page}
                trackingData={{
                  position: key + 1,
                  sectionHeader: sectionHeaderText,
                  userLocation
                }}
                onShareClick={handleShareClick}
                shareTitle={SHARE_TITLE}
                shareUrl={getDealUrl(item.urlSlug)}
              />
            </DealContainer>
          ))}
        </Slider>
      </div>
    </div>
  )
}

const mapStateToProps = (state: ReduxState) => ({
  isMobile: state.mq.lessThan.medium
})

export default connect(mapStateToProps)(FeedSliderGraph)
